import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../redux/store";
import { StoreState } from "../redux/reducers";
import Modal from "../components/Modal";
import CollapseCard from "../components/custom/CollapseCard";
import PageContainer from "../components/custom/PageContainer";
import Main from "../components/custom/Main";
import { useToast } from "../components/toast/ToastProvider";
import { Page } from "../types/Page";
import {
  addPage,
  deletePageById,
  editPage,
  fetchPages,
  fetchPagesByProject,
} from "../redux/actions/pageAction";
import PageForm from "../components/PageForm";
import PageList from "../components/PageList";
import PageFilter from "../components/PageFilter";

const Pages: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();

  const pages = useSelector((state: StoreState) => state.page);
  const [selected, setSelected] = useState<Page | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSave = async (pg: Page) => {
    setLoading(true);
    try {
      if (selected) {
        await dispatch(editPage(pg.id, pg));
        toast("Page updated successfully");
      } else {
        await dispatch(addPage(pg));
        toast("Page added successfully");
      }
      dispatch(fetchPages());
      setSelected(null);
      setShowModal(false);
    } catch (error) {
      toast("Error saving Page");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this Page?")) {
      setLoading(true);
      try {
        await dispatch(deletePageById(id));
        toast("Page deleted successfully");
      } catch (error) {
        toast("Error deleting Page");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (pg: Page) => {
    setSelected(pg);
    setShowModal(true);
  };

  useEffect(() => {
    setLoading(true);
    dispatch(fetchPages()).finally(() => setLoading(false));
  }, [dispatch]);

  const [filters, setFilters] = useState({
    project: "",
  });

  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [isFiltered, setIsFiltered] = useState(false);

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleFindFilters = () => {
    setAppliedFilters(filters);
    setIsFiltered(true);
    localStorage.setItem("pageFilter", JSON.stringify(filters));
    if (filters.project) {
      dispatch(fetchPagesByProject(parseInt(filters.project)));
    } else {
      dispatch(fetchPages());
    }
  };

  const handleResetFilters = () => {
    const emptyFilters = {
      project: "",
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setIsFiltered(false);
    localStorage.removeItem("PageFilter");
  };

  useEffect(() => {
    const savedFilters = localStorage.getItem("PageFilters");
    if (savedFilters) {
      const parsed = JSON.parse(savedFilters);
      setFilters(parsed);
      setAppliedFilters(parsed);
      setIsFiltered(true);
      if (parsed.project) {
        dispatch(fetchPagesByProject(parseInt(parsed.project)));
      } else {
        dispatch(fetchPages());
      }
    } else {
      dispatch(fetchPages());
    }
  }, []);

  return (
    <Main>
      <PageContainer
        title="Page Management"
        buttonText={loading ? "Loading..." : "ADD"}
        onButtonClick={() => setShowModal(true)}
      >
        <Modal
          visible={showModal}
          size="xl"
          onClose={() => {
            setShowModal(false);
            setSelected(null);
          }}
          headerText={selected ? "Update Page" : "Add Page"}
        >
          <PageForm
            initialData={selected}
            onSave={handleSave}
            clearAfterSubmit={() => setSelected(null)}
          />
        </Modal>
        <CollapseCard title="Page Filter">
          <PageFilter
            filters={filters}
            onFilterChange={handleFilterChange}
            onFind={handleFindFilters}
            onReset={handleResetFilters}
          />
        </CollapseCard>
        <CollapseCard title="Page List">
          <PageList pages={pages} onEdit={handleEdit} onDelete={handleDelete} />
        </CollapseCard>
      </PageContainer>
    </Main>
  );
};

export default Pages;
